import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" } }),
};

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Please enter credentials."); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      const token = res.data.access_token;
      if (token) {
        localStorage.setItem("auth_token", token);
      }
      // Resolve role via /auth/me so editors land on the restricted /editor
      // portal instead of the full admin command center.
      let role = "admin";
      try {
        const me = await axios.get(`${API}/auth/me`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
        });
        role = me.data?.role || "admin";
      } catch { /* fall through to default admin route */ }
      if (role === "editor") {
        navigate("/editor", { state: { token } });
      } else {
        navigate("/admin/dashboard", { state: { token } });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="pt-20 min-h-screen bg-luxury-black flex items-center justify-center">
      <motion.div initial="hidden" animate="visible" className="w-full max-w-md px-6">
        <motion.div variants={fadeUp} custom={0} className="text-center mb-10">
          <Lock size={32} className="text-gold mx-auto mb-4" />
          <h1 className="font-heading text-3xl text-white mb-2">Admin Access</h1>
          <p className="text-white/40 font-body text-sm">Authorized personnel only</p>
        </motion.div>

        <motion.form variants={fadeUp} custom={1} onSubmit={handleLogin} className="space-y-6" data-testid="admin-login-form">
          <div>
            <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 block">Email</label>
            <Input
              data-testid="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@aristocrats.com"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm"
            />
          </div>
          <div>
            <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 block">Password</label>
            <Input
              data-testid="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-gold text-black font-body font-medium text-sm px-8 py-4 hover:bg-gold-light transition-colors disabled:opacity-50"
          >
            {loading ? "Authenticating..." : "Sign In"} <ArrowRight size={16} />
          </button>
        </motion.form>
      </motion.div>
    </div>
  );
}
